import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Alert, CircularProgress, Tooltip, LinearProgress, Tabs, Tab,
  Stepper, Step, StepLabel, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  Add, Send, Delete, Refresh, WhatsApp, Campaign,
  ArrowBack, ArrowForward, CheckCircle, Schedule,
  ErrorOutline, People, BarChart, Visibility,
} from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => parseInt(n || 0).toLocaleString();
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('es-EC') : '—';

// ── Estado chip ───────────────────────────────────────────────────────────────
function EstadoChip({ estado }) {
  const map = {
    BORRADOR:   { label: 'Borrador',   bg: '#f5f5f5',  color: '#757575' },
    ENVIANDO:   { label: 'Enviando…',  bg: '#e3f2fd',  color: '#1565c0' },
    COMPLETADA: { label: 'Completada', bg: '#e8f5e9',  color: '#2e7d32' },
    CANCELADA:  { label: 'Cancelada',  bg: '#fce4ec',  color: '#b71c1c' },
  };
  const s = map[estado] || map.BORRADOR;
  return <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: 11 }} />;
}

// ── Barra de progreso de envío ────────────────────────────────────────────────
function ProgresoCampana({ campana }) {
  if (!campana.total_destinatarios) return null;
  const pct = Math.round(((campana.enviados || 0) + (campana.fallidos || 0)) / campana.total_destinatarios * 100);
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
          {campana.enviados || 0} enviados · {campana.fallidos || 0} fallidos
        </Typography>
        <Typography sx={{ fontSize: 10, fontWeight: 700 }}>{pct}%</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct}
        sx={{ height: 4, borderRadius: 2, bgcolor: '#e0e0e0',
          '& .MuiLinearProgress-bar': { bgcolor: campana.fallidos > (campana.enviados || 0) * 0.1 ? '#ef5350' : '#25d366' } }} />
    </Box>
  );
}

// ── Vista detalle de campaña ──────────────────────────────────────────────────
function DetalleCampana({ campana, onBack, onEnviar, enviando }) {
  const [tab, setTab] = useState(0);
  const exitosos = (campana.resultados || []).filter(r => r.estado === 'ENVIADO');
  const fallidos = (campana.resultados || []).filter(r => r.estado === 'FALLIDO');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={onBack} size="small" sx={{ border: '1px solid #e0e0e0' }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800 }}>{campana.nombre}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
            <EstadoChip estado={campana.estado} />
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Creada: {fmtDate(campana.created_at)}
            </Typography>
          </Box>
        </Box>
        {(campana.estado === 'BORRADOR') && (
          <Button variant="contained" startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <Send />}
            onClick={() => onEnviar(campana.id)} disabled={enviando}
            sx={{ bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' }, textTransform: 'none', fontWeight: 700 }}>
            {enviando ? 'Enviando…' : `Enviar a ${fmt(campana.total_destinatarios)} contactos`}
          </Button>
        )}
      </Box>

      {/* KPIs */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
        {[
          { label: 'Destinatarios', value: fmt(campana.total_destinatarios), color: '#1565c0', bg: '#e3f2fd' },
          { label: 'Enviados', value: fmt(campana.enviados), color: '#2e7d32', bg: '#e8f5e9' },
          { label: 'Fallidos', value: fmt(campana.fallidos), color: '#b71c1c', bg: '#fce4ec' },
          { label: 'Tasa éxito', value: campana.total_destinatarios > 0 ? `${Math.round((campana.enviados || 0) / campana.total_destinatarios * 100)}%` : '—', color: '#e65100', bg: '#fff3e0' },
        ].map(({ label, value, color, bg }) => (
          <Paper key={label} sx={{ p: 2, borderRadius: 2, borderTop: `3px solid ${color}`, bgcolor: bg }}>
            <Typography sx={{ fontSize: 10, textTransform: 'uppercase', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>{label}</Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color }}>{value}</Typography>
          </Paper>
        ))}
      </Box>

      {campana.estado !== 'BORRADOR' && <ProgresoCampana campana={campana} />}

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontSize: 13 } }}>
        <Tab label="Configuración" />
        {campana.resultados?.length > 0 && <Tab label={`Resultados (${campana.resultados.length})`} />}
      </Tabs>

      {tab === 0 && (
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 700 }}>Tipo</Typography>
              <Typography sx={{ fontSize: 14 }}>{campana.tipo}</Typography>
            </Box>
            {campana.plantilla_nombre && (
              <Box>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 700 }}>Plantilla</Typography>
                <Typography sx={{ fontSize: 14 }}>{campana.plantilla_nombre} ({campana.plantilla_idioma})</Typography>
              </Box>
            )}
            {campana.mensaje_texto && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 700 }}>Mensaje</Typography>
                <Paper sx={{ p: 1.5, bgcolor: '#f9f9f9', mt: 0.5 }}>
                  <Typography sx={{ fontSize: 13 }}>{campana.mensaje_texto}</Typography>
                </Paper>
              </Box>
            )}
            {campana.categorias_destino?.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 700 }}>Categorías destino</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                  {campana.categorias_destino.map(c => <Chip key={c} label={c} size="small" />)}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {tab === 1 && campana.resultados?.length > 0 && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 700, fontSize: 12 } }}>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Teléfono</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campana.resultados.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontSize: 12 }}>{r.nombre || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{r.telefono}</TableCell>
                    <TableCell>
                      <Chip label={r.estado} size="small"
                        sx={{ bgcolor: r.estado === 'ENVIADO' ? '#e8f5e9' : '#fce4ec', color: r.estado === 'ENVIADO' ? '#2e7d32' : '#b71c1c', fontWeight: 700, fontSize: 10 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: '#b71c1c' }}>{r.error || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Campanas() {
  const [campanas, setCampanas] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [loadingPlantillas, setLoadingPlantillas] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [detalle, setDetalle] = useState(null); // campaña activa en vista detalle
  const [enviando, setEnviando] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  // Wizard state
  const [wiz, setWiz] = useState({
    nombre: '',
    tipo: 'PLANTILLA', // PLANTILLA | TEXTO
    plantilla_nombre: '',
    plantilla_idioma: 'es',
    componentes: [],
    mensaje_texto: '',
    destino: 'CATEGORIAS', // CATEGORIAS | TODOS
    categorias_destino: [],
    contactos_ids: [],
  });

  const [selContactos, setSelContactos] = useState([]);

  useEffect(() => { cargarTodo(); }, []);

  const cargarTodo = async () => {
    try {
      setLoading(true);
      const [campRes, contRes, catRes] = await Promise.all([
        axios.get(`${API}/api/whatsapp/campanas`, cfg()),
        axios.get(`${API}/api/whatsapp/contactos`, cfg()),
        axios.get(`${API}/api/whatsapp/contactos/categorias`, cfg()),
      ]);
      setCampanas(campRes.data.data || []);
      setContactos(contRes.data.data || []);
      setCategorias(catRes.data.data || []);
    } catch (e) {
      setError('Error al cargar campañas');
    } finally { setLoading(false); }
  };

  const cargarPlantillas = async () => {
    setLoadingPlantillas(true);
    try {
      const r = await axios.get(`${API}/api/whatsapp/plantillas`, cfg());
      setPlantillas(r.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Error al cargar plantillas. Verifica el WABA ID en la configuración.');
    } finally { setLoadingPlantillas(false); }
  };

  const abrirWizard = () => {
    setWiz({ nombre: '', tipo: 'PLANTILLA', plantilla_nombre: '', plantilla_idioma: 'es', componentes: [], mensaje_texto: '', destino: 'CATEGORIAS', categorias_destino: [], contactos_ids: [] });
    setSelContactos([]);
    setWizardStep(0);
    setWizardOpen(true);
    cargarPlantillas();
  };

  const destinatariosPreview = () => {
    if (wiz.destino === 'TODOS') return contactos.filter(c => c.activo !== false);
    if (wiz.destino === 'CONTACTOS') return contactos.filter(c => selContactos.includes(c.id));
    return contactos.filter(c => c.activo !== false && wiz.categorias_destino.some(cat => c.categorias?.includes(cat)));
  };

  const crearYEnviar = async () => {
    try {
      setEnviando(true);
      const dest = destinatariosPreview();
      const payload = {
        ...wiz,
        contactos_ids: wiz.destino === 'CONTACTOS' ? selContactos : [],
        categorias_destino: wiz.destino === 'CATEGORIAS' ? wiz.categorias_destino : wiz.destino === 'TODOS' ? [] : wiz.categorias_destino,
      };
      // Si destino es TODOS, limpiar categorias para que el backend tome todos
      if (wiz.destino === 'TODOS') {
        payload.categorias_destino = categorias; // todas las categorías = todos los contactos
        payload.contactos_ids = dest.map(c => c.id);
      }

      const r = await axios.post(`${API}/api/whatsapp/campanas`, payload, cfg());
      const campana = r.data.data;
      setWizardOpen(false);

      // Enviar inmediatamente
      const r2 = await axios.post(`${API}/api/whatsapp/campanas/${campana.id}/enviar`, {}, cfg());
      setSuccess(`🚀 Campaña "${campana.nombre}" enviándose a ${dest.length} contactos`);
      setTimeout(() => setSuccess(null), 6000);
      cargarTodo();
    } catch (e) {
      setError(e.response?.data?.message || 'Error al crear/enviar campaña');
    } finally { setEnviando(false); }
  };

  const enviarCampana = async (campanaId) => {
    try {
      setEnviando(true);
      const r = await axios.post(`${API}/api/whatsapp/campanas/${campanaId}/enviar`, {}, cfg());
      setSuccess(`🚀 ${r.data.message}`);
      setTimeout(() => setSuccess(null), 5000);
      // Recargar campaña en detalle
      const updated = await axios.get(`${API}/api/whatsapp/campanas/${campanaId}`, cfg());
      setDetalle(updated.data.data);
      cargarTodo();
    } catch (e) {
      setError(e.response?.data?.message || 'Error al enviar');
    } finally { setEnviando(false); }
  };

  const eliminarCampana = async () => {
    try {
      await axios.delete(`${API}/api/whatsapp/campanas/${confirmEliminar.id}`, cfg());
      setConfirmEliminar(null);
      setSuccess('Campaña eliminada');
      cargarTodo();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) { setError(e.response?.data?.message || 'Error al eliminar'); }
  };

  const toggleCategoria = (cat) => {
    setWiz(w => ({
      ...w,
      categorias_destino: w.categorias_destino.includes(cat)
        ? w.categorias_destino.filter(c => c !== cat)
        : [...w.categorias_destino, cat],
    }));
  };

  const toggleContacto = (id) => {
    setSelContactos(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // ── Wizard steps ──────────────────────────────────────────────────────────
  const wizardSteps = ['Nombre y tipo', 'Mensaje', 'Destinatarios', 'Revisar y enviar'];

  const canNextStep0 = wiz.nombre.trim().length > 0;
  const canNextStep1 = wiz.tipo === 'PLANTILLA' ? !!wiz.plantilla_nombre : wiz.mensaje_texto.trim().length > 0;
  const canNextStep2 = destinatariosPreview().length > 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#25d366' }} />
      </Box>
    );
  }

  // Vista detalle de una campaña
  if (detalle) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
        <DetalleCampana
          campana={detalle}
          onBack={() => { setDetalle(null); cargarTodo(); }}
          onEnviar={enviarCampana}
          enviando={enviando}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1b5e20' }}>
            <Campaign sx={{ mr: 1, verticalAlign: 'middle', color: '#25d366' }} />
            Campañas WhatsApp
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Envía mensajes masivos con plantillas aprobadas por Meta · {contactos.length} contactos disponibles
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={cargarTodo} title="Actualizar">
            <Refresh fontSize="small" />
          </IconButton>
          <Button variant="contained" startIcon={<Add />} onClick={abrirWizard}
            sx={{ textTransform: 'none', bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' }, fontWeight: 700 }}>
            Nueva campaña
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Sin campañas */}
      {campanas.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <Campaign sx={{ fontSize: 56, color: '#e0e0e0', mb: 2 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1 }}>Sin campañas aún</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 3 }}>
            Crea tu primera campaña para enviar mensajes masivos a tus contactos de WhatsApp
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={abrirWizard}
            sx={{ textTransform: 'none', bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' } }}>
            Crear primera campaña
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#e8f5e9', fontWeight: 700, fontSize: 12 } }}>
                  <TableCell>Campaña</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Plantilla / Mensaje</TableCell>
                  <TableCell align="center">Destinatarios</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campanas.map(c => (
                  <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetalle(c)}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>{c.nombre}</TableCell>
                    <TableCell>
                      <Chip label={c.tipo} size="small" sx={{ fontSize: 10, height: 18 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.plantilla_nombre || c.mensaje_texto || '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{fmt(c.total_destinatarios)}</Typography>
                        {c.estado !== 'BORRADOR' && <ProgresoCampana campana={c} />}
                      </Box>
                    </TableCell>
                    <TableCell><EstadoChip estado={c.estado} /></TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{fmtDate(c.created_at)}</TableCell>
                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                      <Tooltip title="Ver detalle">
                        <IconButton size="small" onClick={() => setDetalle(c)}>
                          <Visibility sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {c.estado === 'BORRADOR' && (
                        <>
                          <Tooltip title="Enviar ahora">
                            <IconButton size="small" onClick={() => { setDetalle(c); }}>
                              <Send sx={{ fontSize: 16, color: '#25d366' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => setConfirmEliminar(c)}>
                              <Delete sx={{ fontSize: 16, color: '#ef5350' }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Wizard nueva campaña ────────────────────────────────────────────── */}
      <Dialog open={wizardOpen} onClose={() => setWizardOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WhatsApp sx={{ color: '#25d366' }} />
            Nueva campaña WhatsApp
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {/* Stepper */}
          <Stepper activeStep={wizardStep} sx={{ mb: 3 }}>
            {wizardSteps.map(label => (
              <Step key={label}><StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: 12 } }}>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {/* Step 0: nombre y tipo */}
          {wizardStep === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField fullWidth size="small" label="Nombre de la campaña *"
                value={wiz.nombre} onChange={e => setWiz(w => ({ ...w, nombre: e.target.value }))}
                placeholder="Ej: Promoción Octubre 2026" />
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>Tipo de mensaje</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {[
                    { tipo: 'PLANTILLA', label: '📋 Plantilla aprobada', desc: 'Usa una plantilla aprobada por Meta. Recomendado para contactos que no han iniciado conversación en las últimas 24h.' },
                    { tipo: 'TEXTO', label: '💬 Mensaje de texto', desc: 'Texto libre. Solo funciona dentro de la ventana de 24h después de que el contacto te haya escrito.' },
                  ].map(({ tipo, label, desc }) => (
                    <Paper key={tipo} onClick={() => setWiz(w => ({ ...w, tipo }))}
                      sx={{ p: 2, borderRadius: 2, cursor: 'pointer', border: `2px solid ${wiz.tipo === tipo ? '#25d366' : '#e0e0e0'}`, bgcolor: wiz.tipo === tipo ? '#f1fff4' : 'white' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>{label}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{desc}</Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {/* Step 1: mensaje */}
          {wizardStep === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {wiz.tipo === 'PLANTILLA' ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Plantillas aprobadas</Typography>
                    <Button size="small" startIcon={loadingPlantillas ? <CircularProgress size={14} /> : <Refresh />}
                      onClick={cargarPlantillas} disabled={loadingPlantillas} sx={{ textTransform: 'none', fontSize: 11 }}>
                      Recargar
                    </Button>
                  </Box>
                  {loadingPlantillas ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={32} /></Box>
                  ) : plantillas.length === 0 ? (
                    <Alert severity="warning">
                      Sin plantillas aprobadas. Crea y aprueba plantillas en Meta Business Suite primero.
                      Asegúrate de tener el WABA ID configurado en WhatsApp → Configuración.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: 300, overflow: 'auto' }}>
                      {plantillas.map(p => (
                        <Paper key={p.name} onClick={() => setWiz(w => ({ ...w, plantilla_nombre: p.name, plantilla_idioma: p.language || 'es' }))}
                          sx={{ p: 2, borderRadius: 2, cursor: 'pointer', border: `2px solid ${wiz.plantilla_nombre === p.name ? '#25d366' : '#e0e0e0'}`, bgcolor: wiz.plantilla_nombre === p.name ? '#f1fff4' : 'white' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{p.name}</Typography>
                              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Idioma: {p.language} · Categoría: {p.category}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Chip label="APROBADA" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: 9, fontWeight: 700 }} />
                            </Box>
                          </Box>
                          {/* Preview del cuerpo de la plantilla */}
                          {p.components?.find(c => c.type === 'BODY') && (
                            <Paper sx={{ p: 1.5, bgcolor: '#f9f9f9', mt: 1 }}>
                              <Typography sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                                {p.components.find(c => c.type === 'BODY')?.text}
                              </Typography>
                            </Paper>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  )}
                  {wiz.plantilla_nombre && (
                    <TextField fullWidth size="small" label="Código de idioma"
                      value={wiz.plantilla_idioma}
                      onChange={e => setWiz(w => ({ ...w, plantilla_idioma: e.target.value }))}
                      helperText="Código ISO: es, en, pt, etc." />
                  )}
                </>
              ) : (
                <TextField fullWidth multiline rows={5} size="small"
                  label="Mensaje de texto *"
                  value={wiz.mensaje_texto}
                  onChange={e => setWiz(w => ({ ...w, mensaje_texto: e.target.value }))}
                  placeholder="Escribe el mensaje que recibirán todos los destinatarios..."
                  helperText={`${wiz.mensaje_texto.length} caracteres`} />
              )}
            </Box>
          )}

          {/* Step 2: destinatarios */}
          {wizardStep === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>Seleccionar destinatarios</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {[
                    { v: 'CATEGORIAS', label: 'Por categoría' },
                    { v: 'TODOS', label: `Todos (${contactos.filter(c => c.activo !== false).length})` },
                    { v: 'CONTACTOS', label: 'Selección manual' },
                  ].map(({ v, label }) => (
                    <Chip key={v} label={label} clickable
                      onClick={() => setWiz(w => ({ ...w, destino: v }))}
                      sx={{ fontWeight: 700, bgcolor: wiz.destino === v ? '#25d366' : '#f5f5f5', color: wiz.destino === v ? 'white' : 'text.primary' }} />
                  ))}
                </Box>
              </Box>

              {wiz.destino === 'CATEGORIAS' && (
                <Box>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>Selecciona una o más categorías:</Typography>
                  {categorias.length === 0 ? (
                    <Alert severity="info" sx={{ fontSize: 12 }}>Sin categorías. Agrega categorías a tus contactos primero.</Alert>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {categorias.map(cat => {
                        const count = contactos.filter(c => c.categorias?.includes(cat) && c.activo !== false).length;
                        return (
                          <Chip key={cat} label={`${cat} (${count})`} clickable
                            onClick={() => toggleCategoria(cat)}
                            sx={{ fontWeight: 700, bgcolor: wiz.categorias_destino.includes(cat) ? '#25d366' : '#f5f5f5', color: wiz.categorias_destino.includes(cat) ? 'white' : 'text.primary' }} />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {wiz.destino === 'CONTACTOS' && (
                <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{selContactos.length} seleccionados</Typography>
                    <Button size="small" sx={{ textTransform: 'none', fontSize: 11 }}
                      onClick={() => setSelContactos(selContactos.length === contactos.length ? [] : contactos.map(c => c.id))}>
                      {selContactos.length === contactos.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                    </Button>
                  </Box>
                  {contactos.filter(c => c.activo !== false).map(c => (
                    <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                      <Checkbox size="small" checked={selContactos.includes(c.id)} onChange={() => toggleContacto(c.id)} />
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 13 }}>{c.nombre || c.telefono}</Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'monospace' }}>{c.telefono}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {(c.categorias || []).slice(0, 2).map(cat => <Chip key={cat} label={cat} size="small" sx={{ fontSize: 9, height: 16 }} />)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Preview destinatarios */}
              <Paper sx={{ p: 1.5, bgcolor: destinatariosPreview().length > 0 ? '#f1fff4' : '#fff8e1', borderRadius: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: destinatariosPreview().length > 0 ? '#2e7d32' : '#f57f17' }}>
                  {destinatariosPreview().length > 0
                    ? `✅ ${destinatariosPreview().length} destinatarios seleccionados`
                    : '⚠️ Sin destinatarios — selecciona al menos uno'}
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Step 3: revisar */}
          {wizardStep === 3 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info" sx={{ fontSize: 12 }}>
                Revisa los detalles antes de enviar. El envío es inmediato y no se puede cancelar una vez iniciado.
              </Alert>
              {[
                { label: 'Nombre', value: wiz.nombre },
                { label: 'Tipo', value: wiz.tipo },
                { label: 'Plantilla / Mensaje', value: wiz.plantilla_nombre || wiz.mensaje_texto },
                { label: 'Destinatarios', value: `${destinatariosPreview().length} contactos` },
                { label: 'Categorías', value: wiz.categorias_destino.join(', ') || (wiz.destino === 'TODOS' ? 'Todos' : 'Selección manual') },
              ].map(({ label, value }) => value && (
                <Box key={label} sx={{ display: 'flex', gap: 2, borderBottom: '1px solid #f0f0f0', pb: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, width: 140, flexShrink: 0, color: 'text.secondary' }}>{label}</Typography>
                  <Typography sx={{ fontSize: 13 }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setWizardOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Box sx={{ flex: 1 }} />
          {wizardStep > 0 && (
            <Button startIcon={<ArrowBack />} onClick={() => setWizardStep(s => s - 1)} sx={{ textTransform: 'none' }}>
              Atrás
            </Button>
          )}
          {wizardStep < 3 ? (
            <Button variant="contained" endIcon={<ArrowForward />}
              onClick={() => setWizardStep(s => s + 1)}
              disabled={wizardStep === 0 ? !canNextStep0 : wizardStep === 1 ? !canNextStep1 : wizardStep === 2 ? !canNextStep2 : false}
              sx={{ textTransform: 'none', bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' } }}>
              Siguiente
            </Button>
          ) : (
            <Button variant="contained" startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <Send />}
              onClick={crearYEnviar} disabled={enviando}
              sx={{ textTransform: 'none', bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' }, fontWeight: 700, px: 3 }}>
              {enviando ? 'Enviando…' : `🚀 Enviar campaña`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminar */}
      <Dialog open={!!confirmEliminar} onClose={() => setConfirmEliminar(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Eliminar campaña</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            ¿Eliminar la campaña <strong>{confirmEliminar?.nombre}</strong>? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmEliminar(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={eliminarCampana} sx={{ textTransform: 'none' }}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
